import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, NgForm } from '@angular/forms';
import Swal from 'sweetalert2';
import { CreditNoteListComponent } from '../list/credit-note-list.component';
import { CreditNoteRestService } from '../credit-note-rest.service';
import { CommonService, DataFormatService } from '../../../libraries/libraries.module';

@Component({
  selector: 'app-add-modal',
  templateUrl: './add-modal.component.html'
})
export class AddModalComponent implements OnInit {
  listComponent: CreditNoteListComponent;
  isModalOpen = false;
  mainDealerId: string;
  isLoading = false;

  // Credit note data model
  creditNoteData = {
    date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    category: '',
    quantity: '',
    revenue: 0,
    deduction: 0,
    remaining: 0,
    description: ''
  };

  // Credit note errors
  creditNoteErrors = {
    deduction: undefined,
    revenue: undefined,
    quantity: undefined
  };

  // Credit note categories
  creditNoteCategories = [
    { value: 'insentive_sell_in', label: 'Insentive Sell In' },
    { value: 'insentive_sell_out', label: 'Insentive Sell Out' }
  ];

  constructor(
    listComponent: CreditNoteListComponent,
    private api: CreditNoteRestService,
    private dataFormatService: DataFormatService
  ) {
    this.listComponent = listComponent;
  }

  formatRupiah = this.dataFormatService.formatRupiah;

  ngOnInit(): void {}

  // Open the modal
  openModal(mainDealerId: string): void {
    this.isModalOpen = true;
    this.mainDealerId = mainDealerId;
  }

  // Close the modal
  closeModal(): void {
    this.resetForm();
    this.isModalOpen = false;
  }

  // Reset form data
  resetForm(): void {
    this.creditNoteData = {
      date: new Date().toISOString().split('T')[0],
      category: '',
      quantity: null,
      revenue: 0,
      deduction: 0,
      remaining: 0,
      description: ''
    };
  }

  // Generic function to handle currency input
  onCurrencyInput(event: any, fieldName: string): void {
    // Get the raw input value
    const inputValue = event.target.value;

    // Parse the input to get the numeric value
    const numericValue = this.dataFormatService.parseRupiahToNumber(inputValue);

    // Update the numeric value in the data model
    this.creditNoteData[fieldName] = numericValue;

    // Format the input value
    event.target.value = this.dataFormatService.formatRupiah(numericValue);

    // Calculate remaining
    this.calculateRemaining();
  }

  // Calculate remaining amount
  calculateRemaining(): void {
    if (this.creditNoteData.revenue < this.creditNoteData.deduction) {
      this.creditNoteErrors.deduction = 'Deduction cannot be greater than revenue';
      this.creditNoteData.remaining = 0; // Reset remaining if deduction is greater
      return;
    }

    const revenue = this.creditNoteData.revenue;
    const deduction = this.creditNoteData.deduction;

    // Store the numeric value for remaining
    this.creditNoteData.remaining = revenue - deduction;

    this.creditNoteErrors.deduction = undefined;
  }

  onValidate() {
    if (this.creditNoteData.revenue < 1) {
      this.creditNoteErrors.revenue = 'Revenue must be greater than 0';
    } else {
      this.creditNoteErrors.revenue = undefined;
    }
    if (this.creditNoteData.deduction < 1) {
      this.creditNoteErrors.deduction = 'Deduction must be greater than 0';
    } else {
      this.creditNoteErrors.deduction = undefined;
    }

    return Object.values(this.creditNoteErrors).some(error => error !== undefined);
  }

  // Submit form
  onSubmit(form: NgForm): void {
    if (form.invalid) {
      return;
    }

    if (this.onValidate()) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fix the errors before submitting'
      });
      return;
    }

    this.isLoading = true;
    this.api
      .create({
        mainDealerId: this.mainDealerId,
        date: this.creditNoteData.date,
        category: this.creditNoteData.category,
        quantity: Number(this.creditNoteData.quantity),
        revenue: this.creditNoteData.revenue,
        deductedAmount: this.creditNoteData.deduction,
        remainingCredit: this.creditNoteData.remaining,
        note: this.creditNoteData.description
      })
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.closeModal();
          this.resetForm();
          this.listComponent.callHookDirectly('init');
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Credit note created successfully'
          });
        },
        error: _error => {
          this.isLoading = false;
          let errorMessage = 'Failed to create credit note';

          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage
          });
        }
      });
  }
}
